import { useEffect, useMemo, useRef } from "preact/hooks";
import { type Choice, type FieldOf, OTHER_CHOICE_ID } from "@fobu/schema";
import { seededShuffle } from "@fobu/engine";
import { useFormEnv } from "../../lib/context";
import { t } from "../../lib/i18n";
import { Markdown } from "../../lib/markdown";
import { type FieldProps, keyFor } from "./types";
import { LazySlots } from "../Common";

export interface Option {
  id: string;
  label: string;
  imageUrl?: string;
  disabled?: boolean;
  badge?: string;
}

export function useOptions(field: FieldOf<"single_choice" | "multiple_choice" | "dropdown">): Option[] {
  const { ctl, remaining } = useFormEnv();
  return useMemo(() => {
    const base: Choice[] = field.randomize ? seededShuffle(field.choices, ctl.seed + field.id) : field.choices;
    const opts: Option[] = base.map((c) => {
      const left = remaining[`${field.id}:${c.id}`];
      const waitlist = "waitlist" in field && field.waitlist;
      return {
        id: c.id,
        label: c.label,
        ...(c.imageUrl ? { imageUrl: c.imageUrl } : {}),
        ...(left !== undefined
          ? { disabled: left <= 0 && !waitlist, badge: left <= 0 ? (waitlist ? t.waitlistBadge : t.soldOut) : left <= 5 ? t.remaining(left) : undefined }
          : {}),
      } as Option;
    });
    if ("allowOther" in field && field.allowOther) opts.push({ id: OTHER_CHOICE_ID, label: t.other });
    return opts;
  }, [field, ctl.seed, remaining]);
}

/** Tastenkürzel A–Z (nur Conversational, nur wenn kein Textfeld fokussiert ist). */
function useLetterShortcuts(enabled: boolean, options: Option[], onPick: (o: Option) => void) {
  const pick = useRef(onPick);
  pick.current = onPick;
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.key.length !== 1) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) && (target as HTMLInputElement).type !== "radio" && (target as HTMLInputElement).type !== "checkbox")) return;
      const i = e.key.toUpperCase().charCodeAt(0) - 65;
      const opt = options[i];
      if (opt && !opt.disabled) {
        e.preventDefault();
        pick.current(opt);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [enabled, options]);
}

function OptionList({
  name,
  type,
  options,
  selected,
  onToggle,
  layout,
  conversational,
}: {
  name: string;
  type: "radio" | "checkbox";
  options: Option[];
  selected: string[];
  onToggle: (o: Option, checked: boolean) => void;
  layout: "list" | "cards" | "images";
  conversational: boolean;
}) {
  return (
    <div class={`fb-options fb-options--${layout}`}>
      {options.map((o, i) => {
        const checked = selected.includes(o.id);
        return (
          <label key={o.id} class={`fb-option${checked ? " is-checked" : ""}${o.disabled ? " is-disabled" : ""}`}>
            <input
              class="fb-option-input"
              type={type}
              name={name}
              value={o.id}
              checked={checked}
              disabled={o.disabled}
              onChange={(e) => onToggle(o, e.currentTarget.checked)}
            />
            {o.imageUrl && <img class="fb-option-img" src={o.imageUrl} alt="" loading="lazy" />}
            {conversational && keyFor(i) && (
              <span class="fb-key" aria-hidden="true">
                {keyFor(i)}
              </span>
            )}
            <span class={`fb-mark fb-mark--${type}`} aria-hidden="true" />
            <span class="fb-option-label">{o.label}</span>
            {o.badge && <span class="fb-badge">{o.badge}</span>}
          </label>
        );
      })}
    </div>
  );
}

function OtherInput({ fieldId, visible }: { fieldId: string; visible: boolean }) {
  const { ctl } = useFormEnv();
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (visible) ref.current?.focus();
  }, [visible]);
  if (!visible) return null;
  return (
    <input
      ref={ref}
      class="fb-input fb-other"
      type="text"
      aria-label={`${t.other}: ${t.otherPlaceholder}`}
      placeholder={t.otherPlaceholder}
      value={ctl.other[fieldId] ?? ""}
      onInput={(e) => ctl.setOtherText(fieldId, e.currentTarget.value)}
      maxLength={500}
    />
  );
}

export function SingleChoice({ field, value, onChange, a11y, conversational, onAdvance }: FieldProps<FieldOf<"single_choice">>) {
  const options = useOptions(field);
  const selected = typeof value === "string" ? [value] : [];
  const choose = (o: Option) => {
    onChange(o.id);
    if (conversational && field.autoAdvance && o.id !== OTHER_CHOICE_ID) onAdvance();
  };
  useLetterShortcuts(conversational && field.layout !== "slots", options, choose);
  const { ctl } = useFormEnv();
  if (field.layout === "slots") return <LazySlots field={field} options={options} lang={ctl.def.settings.language} value={value} onChange={onChange} a11y={a11y} />;
  return (
    <>
      <OptionList name={a11y.id} type="radio" options={options} selected={selected} onToggle={(o) => choose(o)} layout={field.layout} conversational={conversational} />
      <OtherInput fieldId={field.id} visible={value === OTHER_CHOICE_ID} />
    </>
  );
}

export function MultipleChoice({ field, value, onChange, a11y, conversational }: FieldProps<FieldOf<"multiple_choice">>) {
  const options = useOptions(field);
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const toggle = (o: Option, checked: boolean) => {
    const next = checked ? [...selected.filter((x) => x !== o.id), o.id] : selected.filter((x) => x !== o.id);
    // Reihenfolge der Optionen beibehalten, nicht der Klicks.
    const order = options.map((x) => x.id);
    onChange(next.sort((a, b) => order.indexOf(a) - order.indexOf(b)));
  };
  useLetterShortcuts(conversational, options, (o) => toggle(o, !selected.includes(o.id)));
  return (
    <>
      <OptionList name={a11y.id} type="checkbox" options={options} selected={selected} onToggle={toggle} layout={field.layout} conversational={conversational} />
      <OtherInput fieldId={field.id} visible={selected.includes(OTHER_CHOICE_ID)} />
    </>
  );
}

export function Dropdown({ field, value, onChange, a11y }: FieldProps<FieldOf<"dropdown">>) {
  const options = useOptions(field);
  return (
    <div class="fb-select-wrap">
      <select
        class="fb-input fb-select"
        id={a11y.id}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.currentTarget.value || undefined)}
        required={field.required}
        aria-required={field.required}
        aria-invalid={a11y.invalid || undefined}
        aria-describedby={a11y.describedBy}
      >
        <option value="">{t.selectPlaceholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id} disabled={o.disabled}>
            {o.badge ? `${o.label} (${o.badge})` : o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function YesNo({ value, onChange, a11y, conversational, onAdvance }: FieldProps<FieldOf<"yes_no">>) {
  const options: Option[] = [
    { id: "yes", label: t.yes },
    { id: "no", label: t.no },
  ];
  const selected = value === true ? ["yes"] : value === false ? ["no"] : [];
  const choose = (o: Option) => {
    onChange(o.id === "yes");
    if (conversational) onAdvance();
  };
  useLetterShortcuts(conversational, options, choose);
  return <OptionList name={a11y.id} type="radio" options={options} selected={selected} onToggle={choose} layout="cards" conversational={conversational} />;
}

export function Consent({ field, value, onChange, a11y }: FieldProps<FieldOf<"consent">>) {
  const { tpl } = useFormEnv();
  const checked = value === true;
  return (
    <label class={`fb-option fb-consent${checked ? " is-checked" : ""}`}>
      <input
        class="fb-option-input"
        type="checkbox"
        id={a11y.id}
        checked={checked}
        onChange={(e) => onChange(e.currentTarget.checked ? true : undefined)}
        required={field.required}
        aria-required={field.required}
        aria-invalid={a11y.invalid || undefined}
      />
      <span class="fb-mark fb-mark--checkbox" aria-hidden="true" />
      <span class="fb-option-label">
        <Markdown text={tpl(field.consentText)} />
      </span>
    </label>
  );
}
